import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createGuestRequest,
  deleteGuestRequest,
  getGuestsRequest,
  updateGuestRequest,
} from "../../api/guestApi";
import { CreateGuestRequest } from "../../models/RequestModels/CreateGuestRequest";
import { UpdateGuestRequest } from "../../models/RequestModels/UpdateGuestRequest";
import { GuestResponse } from "../../models/ResponseModels/GuestResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface GuestState {
  guests: GuestResponse[];
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: GuestState = {
  guests: [],
  loading: false,
  saving: false,
  error: null,
};

export const loadGuests = createAsyncThunk(
  "guests/loadGuests",
  async (params: { search?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await getGuestsRequest(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createGuest = createAsyncThunk(
  "guests/createGuest",
  async (body: CreateGuestRequest, { rejectWithValue }) => {
    try {
      const response = await createGuestRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateGuest = createAsyncThunk(
  "guests/updateGuest",
  async (
    payload: { id: number; body: UpdateGuestRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateGuestRequest(payload.id, payload.body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteGuest = createAsyncThunk(
  "guests/deleteGuest",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteGuestRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const guestSlice = createSlice({
  name: "guests",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadGuests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadGuests.fulfilled, (state, action) => {
        state.loading = false;
        state.guests = action.payload;
      })
      .addCase(loadGuests.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Could not load guests.",
        );
      })

      .addCase(createGuest.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createGuest.fulfilled, (state, action) => {
        state.saving = false;
        state.guests.unshift(action.payload);
      })
      .addCase(createGuest.rejected, (state, action) => {
        state.saving = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Could not save guest.",
        );
      })

      .addCase(updateGuest.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateGuest.fulfilled, (state, action) => {
        state.saving = false;
        state.guests = state.guests.map((guest) =>
          guest.id === action.payload.id ? action.payload : guest,
        );
      })
      .addCase(updateGuest.rejected, (state, action) => {
        state.saving = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Could not update guest.",
        );
      })

      .addCase(deleteGuest.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteGuest.fulfilled, (state, action) => {
        state.saving = false;
        state.guests = state.guests.filter(
          (guest) => guest.id !== action.payload,
        );
      })
      .addCase(deleteGuest.rejected, (state, action) => {
        state.saving = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Could not delete guest.",
        );
      });
  },
});

export default guestSlice.reducer;
